import axios from 'axios'
import { API_TYPE } from './IOCTypes'
import { AppAuthenticator } from '@auth/AppAuthenticator'
import { IOCContainer, ARCHBASE_IOC_API_TYPE } from '@archbase/core'
import { ArchbaseAccessTokenService, ArchbaseApiTokenService } from '@archbase/security'
import { ArchbaseAxiosRemoteApiClient, ArchbaseRemoteApiClient } from '@archbase/data'
import {
  DefaultArchbaseTokenManager,
  ArchbaseUserService,
  ArchbaseProfileService,
  ArchbaseGroupService,
  ArchbaseResourceService,
} from '@archbase/security'
import { API_URL, STORAGE_KEYS } from '../AppConstants'

/**
 * Container de injeção de dependências.
 *
 * Vazio em produção: o nginx da imagem faz proxy de `/v1/` para o doclenz-api, então as chamadas
 * saem same-origin e não há CORS a resolver.
 */
axios.defaults.baseURL = API_URL

/**
 * O doclenz autentica o admin por `X-Admin-Key`, não por `Authorization: Bearer`.
 *
 * O `AppAuthenticator` devolve a chave de admin disfarçada de access token para o Archbase
 * seguir seu fluxo normal; este interceptor faz o caminho de volta, convertendo-a no header que
 * o `AdminController` espera. Nenhum service precisa conhecer a credencial.
 *
 * Requisições para `/v1/extract` são deixadas em paz de propósito: o playground usa uma API key
 * de tenant (`X-API-Key`), e mandar junto um `X-Admin-Key` só confundiria o log de acesso.
 */
axios.interceptors.request.use((config) => {
  const url = config.url || ''
  if (url.includes('/v1/extract')) {
    return config
  }
  const adminKey = localStorage.getItem(STORAGE_KEYS.ADMIN_KEY)
  if (adminKey) {
    config.headers = config.headers || {}
    ;(config.headers as Record<string, string>)['X-Admin-Key'] = adminKey
    // O doclenz não lê Authorization; deixá-lo só polui a requisição.
    delete (config.headers as Record<string, string>)['Authorization']
  }
  return config
})

const container = IOCContainer.getContainer()

IOCContainer.registerTokenManager(DefaultArchbaseTokenManager)

// O doclenz não tem backend de usuários/grupos/perfis. Os serviços são registrados porque o
// shell do Archbase os resolve na inicialização; as telas que dependeriam deles (perfil, troca
// de senha, permissões) estão fora do console — ver README, seção "Autenticação".
IOCContainer.registerSecurityServices({
  userService: ArchbaseUserService,
  profileService: ArchbaseProfileService,
  groupService: ArchbaseGroupService,
  resourceService: ArchbaseResourceService,
  accessTokenService: ArchbaseAccessTokenService,
  apiTokenService: ArchbaseApiTokenService,
})

if (IOCContainer.hasService(ARCHBASE_IOC_API_TYPE.Authenticator)) {
  IOCContainer.unbindService(ARCHBASE_IOC_API_TYPE.Authenticator)
}
IOCContainer.registerService(ARCHBASE_IOC_API_TYPE.Authenticator, AppAuthenticator)

container.bind<ArchbaseRemoteApiClient>(API_TYPE.ApiClient).to(ArchbaseAxiosRemoteApiClient)

// ── Serviços do doclenz ──────────────────────────────────────────────────────
import { DoclenzKeyService } from '@services/DoclenzKeyService'
import { DoclenzUsageService } from '@services/DoclenzUsageService'
import { DoclenzJobService } from '@services/DoclenzJobService'
import { DoclenzSettingsService } from '@services/DoclenzSettingsService'
import { DoclenzAuditService } from '@services/DoclenzAuditService'
import { DoclenzExtractService } from '@services/DoclenzExtractService'

container.bind<DoclenzKeyService>(API_TYPE.DoclenzKey).to(DoclenzKeyService)
container.bind<DoclenzUsageService>(API_TYPE.DoclenzUsage).to(DoclenzUsageService)
container.bind<DoclenzJobService>(API_TYPE.DoclenzJob).to(DoclenzJobService)
container.bind<DoclenzSettingsService>(API_TYPE.DoclenzSettings).to(DoclenzSettingsService)
container.bind<DoclenzAuditService>(API_TYPE.DoclenzAudit).to(DoclenzAuditService)
container.bind<DoclenzExtractService>(API_TYPE.DoclenzExtract).to(DoclenzExtractService)

export default container
